// PostMyNeedScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Alert,
  StyleSheet,
  Pressable,
  Platform,
  PermissionsAndroid,
  ScrollView,
  KeyboardAvoidingView,
} from 'react-native';
import Geolocation from 'react-native-geolocation-service';
import mystyles from '../../assets/styles/mystyles';
import {callApi, getUserId, requestLocationPermission} from '../Helper/Helper';
import poststyles from '../../assets/styles/poststyles';

// Replace with your actual API base URL or import from your config
const ENDPOINT_URL = 'https://api.eabu.net';



const TABS = {
  FOOD: 'Food',
  HOUSEHOLD: 'Household',
  TRANSPORT: 'Transport',
};

const STEPS = {
  ITEM: 0,
  DETAILS: 1,
  ZIP: 2,
};

export default function ConsumerPostNeed() {
  const [activeTab, setActiveTab] = useState(TABS.FOOD);

  const [step, setStep] = useState(STEPS.ITEM);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [zipcode, setZipcode] = useState('');

  const [coords, setCoords] = useState(null); // { latitude, longitude }
  const [submitting, setSubmitting] = useState(false);

  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({ title: '', description: '', zipcode: '' });

  const TITLE_MAX = 25;
  const DESC_MAX = 250;

  const tabConfig = useMemo(() => {
    const themeColor = '#0A84FF';

    switch (activeTab) {
      case TABS.FOOD:
        return {
          heading: '',
          titleLabel: 'I am looking for:',
          titlePlaceholder: 'e.g., Homemade pasta, Vegetarian meal prep',
          descriptionLabel: 'Details (Recipe, Budget etc)',
          descriptionPlaceholder:
            'Examples:\n• Grilled chicken (I can share a recipe)\n• Chinese food\n• Vegetarian pasta with veggies only',
          endpoint: '/eabu-eat-consumer',
          btnColor: themeColor,
          btnLabel: 'Submit',
          quickTitles: [],
        };

      case TABS.HOUSEHOLD:
        return {
          heading: '',
          titleLabel: 'I am looking for:',
          titlePlaceholder: 'e.g., Yard cleanup, Dog walker, House cleaning',
          descriptionLabel: 'Details (size, duration, supplies, timing)',
          descriptionPlaceholder:
            'Examples:\n• Yard cleanup\n• Dog walker for a few hours\n• Tennis partner\n• Floral decoration for an event',
          endpoint: '/eabu-live-consumer',
          btnColor: themeColor,
          btnLabel: 'Submit Household Request',
          quickTitles: [],
        };

      case TABS.TRANSPORT:
        return {
          heading: '',
          titleLabel: 'I am looking for:',
          titlePlaceholder: 'e.g., Help moving apartment, Pickup & delivery',
          descriptionLabel: 'Details (pickup/dropoff, size, date/time)',
          descriptionPlaceholder:
            'Examples:\n• Help moving to a new house\n• Pickup & delivery (furniture)\n• Car help (used car search / oil change)',
          endpoint: '/eabu-move-consumer',
          btnColor: themeColor,
          btnLabel: 'Submit Transport Request',
          quickTitles: [],
        };

      default:
        return {
          heading: 'Request',
          titleLabel: 'Title',
          titlePlaceholder: '',
          descriptionLabel: 'Description',
          descriptionPlaceholder: '',
          endpoint: '',
          btnColor: '#ccc',
          btnLabel: 'Submit',
          quickTitles: [],
        };
    }
  }, [activeTab]);

  const [userId, setUserId] = useState(null);

  const buttonStyle = (activeColor, disabled) => ({
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: disabled ? '#E5E7EB' : activeColor,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: disabled ? 0 : 0.12,
    shadowRadius: 10,
    elevation: disabled ? 0 : 3,
  });

  const secondaryButtonStyle = (disabled) => ({
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: disabled ? '#F3F4F6' : '#FFFFFF',
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
    borderWidth: 1,
    borderColor: disabled ? '#E5E7EB' : '#D1D5DB',
  });

  useEffect(() => {
    (async () => {
      try {
        const id = await getUserId();
        setUserId(id);
      } catch (e) {
        console.warn('Failed to get user id', e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const hasPermission = await requestLocationPermission();
      if (!hasPermission) {
        setCoords({ latitude: 0, longitude: 0 });
        return;
      }

      Geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoords({ latitude, longitude });
        },
        (error) => {
          Alert.alert('Location error', error?.message || 'Unable to get your location. Defaulting to 0,0.');
          setCoords({ latitude: 0, longitude: 0 });
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 10000,
          forceRequestLocation: true,
          showLocationDialog: true,
        }
      );
    })();
  }, []);

  useEffect(() => {
    // reset fields when switching service type (prevents accidental cross-category submissions)
    setStep(STEPS.ITEM);
    setTitle('');
    setDescription('');
    setZipcode('');
    setMessage('');
    setErrors({ title: '', description: '', zipcode: '' });
  }, [activeTab]);

  const validateStep = (s) => {
    const next = { title: '', description: '', zipcode: '' };

    const t = title.trim();
    const d = description.trim();
    const z = zipcode.trim();

    if (s === STEPS.ITEM || s === STEPS.DETAILS || s === STEPS.ZIP) {
      if (!t) next.title = 'Please enter an item name.';
      else if (t.length > TITLE_MAX) next.title = `Max ${TITLE_MAX} characters.`;
    }

    if (s === STEPS.DETAILS || s === STEPS.ZIP) {
      if (!d) next.description = 'Please provide details.';
      else if (d.length > DESC_MAX) next.description = `Max ${DESC_MAX} characters.`;
    }

    if (s === STEPS.ZIP) {
      if (!z) next.zipcode = 'Please enter a ZIP code.';
      else if (z.length !== 5) next.zipcode = 'ZIP code must be 5 digits.';
    }

    setErrors(next);

    const ok =
      (s === STEPS.ITEM && !next.title) ||
      (s === STEPS.DETAILS && !next.title && !next.description) ||
      (s === STEPS.ZIP && !next.title && !next.description && !next.zipcode);

    setMessage(ok ? '' : 'Please fix the highlighted fields.');
    return ok;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep((prev) => Math.min(prev + 1, STEPS.ZIP));
  };

  const goBack = () => {
    setMessage('');
    setStep((prev) => Math.max(prev - 1, STEPS.ITEM));
  };

      const handleSubmit = async () => {
        if (!validateStep(STEPS.ZIP)) return;

        const lat = coords?.latitude ?? 0;
        const lon = coords?.longitude ?? 0;

        if (lat === 0 || lon === 0) {
          Alert.alert(
            'Location required',
            'Your location was not found, please turn on the location in your Phone'
          );
          return;
        }

        setSubmitting(true);
        try {
          const url = `${ENDPOINT_URL}${tabConfig.endpoint}`;
          const payload = {
            longitude: lon,
            latitude: lat,
            userId: userId,
            zipcode: zipcode.trim(),
            title: title.trim(),
            description: description.trim(),
          };

          await callApi('POST', url, { body: payload });

          Alert.alert(
            'Request submitted',
            'All set — your request is live. Check back soon for offers from people nearby.',
            [{ text: 'Got it', style: 'default' }],
            { cancelable: true }
          );

          setStep(STEPS.ITEM);
          setTitle('');
          setDescription('');
          setZipcode('');
          setErrors({ title: '', description: '', zipcode: '' });
          setMessage('');
        } catch (e) {
          setMessage(e?.message || 'Submission failed. Please try again.');
        } finally {
          setSubmitting(false);
        }
      };

  const stepTitle =
    step === STEPS.ITEM ? 'Step 1 of 3 — Item Name' :
    step === STEPS.DETAILS ? 'Step 2 of 3 — Details' :
    'Step 3 of 3 — ZIP code';

  return (
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
    <ScrollView contentContainerStyle={{ paddingBottom: 24 }} keyboardShouldPersistTaps="handled">
      <View style={mystyles.container}>
        <View style={poststyles.tabContainer}>
          <Pressable
            onPress={() => setActiveTab(TABS.FOOD)}
            style={({pressed})  =>[
              poststyles.tabButton,
              activeTab === TABS.FOOD && poststyles.tabButtonActive,
              pressed && poststyles.tabButtonPressed,
            ]}
            accessibilityRole="tab"
            accessibilityState={{selected: activeTab === TABS.FOOD}}>
            <Text
              style={[
                poststyles.tabText,
                activeTab === TABS.FOOD && poststyles.tabTextActive,
              ]}>
              Food
            </Text>
            <View
              style={[
                poststyles.tabIndicator,
                activeTab === TABS.FOOD && poststyles.tabIndicatorActive,
              ]}
            />
          </Pressable>

          <Pressable
            onPress={() => setActiveTab(TABS.HOUSEHOLD)}
            style={({pressed}) => [
              poststyles.tabButton,
              activeTab === TABS.HOUSEHOLD && poststyles.tabButtonActive,
              pressed && poststyles.tabButtonPressed,
            ]}
            accessibilityRole="tab"
            accessibilityState={{selected: activeTab === TABS.HOUSEHOLD}}>
            <Text
              style={[
                poststyles.tabText,
                activeTab === TABS.HOUSEHOLD && poststyles.tabTextActive,
              ]}>
              Household
            </Text>
            <View
              style={[
                poststyles.tabIndicator,
                activeTab === TABS.HOUSEHOLD && poststyles.tabIndicatorActive,
              ]}
            />
          </Pressable>

          <Pressable
            onPress={() => setActiveTab(TABS.TRANSPORT)}
            style={({pressed}) => [
              poststyles.tabButton,
              activeTab === TABS.TRANSPORT && poststyles.tabButtonActive,
              pressed && poststyles.tabButtonPressed,
            ]}
            accessibilityRole="tab"
            accessibilityState={{selected: activeTab === TABS.TRANSPORT}}>
            <Text
              style={[
                poststyles.tabText,
                activeTab === TABS.TRANSPORT && poststyles.tabTextActive,
              ]}>
              Transport
            </Text>
            <View
              style={[
                poststyles.tabIndicator,
                activeTab === TABS.TRANSPORT && poststyles.tabIndicatorActive,
              ]}
            />
          </Pressable>
        </View>
        <Text style={poststyles.subHeading}>{tabConfig.heading}</Text>

        {/*<View style={poststyles.stepHeader}>*/}
        {/*  <Text style={poststyles.stepTitle}>{stepTitle}</Text>*/}
        {/*</View>*/}

        <View style={{ marginTop: 12 }}>
          {/* Step 1: Item Name */}
          {step === STEPS.ITEM && (
            <>
              <Text style={[poststyles.label]}>{tabConfig.titleLabel}</Text>
              <TextInput
                value={title}
                onChangeText={(v) => {
                  setTitle(v);
                  if (errors.title) setErrors((p) => ({ ...p, title: '' }));
                }}
                placeholder={tabConfig.titlePlaceholder}
                maxLength={TITLE_MAX}
                editable={!submitting}
                style={[poststyles.input, poststyles.inputText, errors.title ? poststyles.inputError : null]}
                placeholderTextColor="#6B7280"
              />
              <View style={poststyles.helperRow}>
                <Text style={poststyles.helperText}>{errors.title || 'Keep it short and specific:'}</Text>
                <Text style={poststyles.counterText}>{title.trim().length}/{TITLE_MAX}</Text>
              </View>

              {!!tabConfig.quickTitles?.length && (
                <View style={poststyles.chipRow}>
                  {tabConfig.quickTitles.map((qt) => (
                    <Pressable
                      key={qt}
                      onPress={() => setTitle(qt)}
                      disabled={submitting}
                      style={({ pressed }) => [poststyles.chip, pressed && !submitting && poststyles.chipPressed]}
                    >
                      <Text style={poststyles.chipText}>{qt}</Text>
                    </Pressable>
                  ))}
                </View>
              )}
            </>
          )}

          {/* Step 2: Details */}
          {step === STEPS.DETAILS && (
            <>
              <Text style={[poststyles.label]}>{tabConfig.descriptionLabel}</Text>
              <TextInput
                value={description}
                onChangeText={(v) => {
                  setDescription(v);
                  if (errors.description) setErrors((p) => ({ ...p, description: '' }));
                }}
                placeholder={tabConfig.descriptionPlaceholder}
                maxLength={DESC_MAX}
                multiline
                numberOfLines={6}
                textAlignVertical="top"
                editable={!submitting}
                style={[
                  poststyles.input,
                  poststyles.inputText,
                  { minHeight: 180, paddingTop: 12, paddingBottom: 12, lineHeight: 22 },
                  errors.description ? poststyles.inputError : null,
                ]}
                placeholderTextColor="#6B7280"
              />
              <View style={poststyles.helperRow}>
                <Text style={poststyles.helperText}>
                  {errors.description || 'Include timing, preferences, and any constraints.'}
                </Text>
                <Text style={poststyles.counterText}>{description.trim().length}/{DESC_MAX}</Text>
              </View>
            </>
          )}

          {/* Step 3: ZIP */}
          {step === STEPS.ZIP && (
            <>
              <Text style={[mystyles.label]}>Your current ZIP code</Text>
              <TextInput
                value={zipcode}
                onChangeText={(t) => {
                  const v = t.replace(/[^0-9]/g, '').slice(0, 5);
                  setZipcode(v);
                  if (errors.zipcode) setErrors((p) => ({ ...p, zipcode: '' }));
                }}
                placeholder="e.g., 94105"
                keyboardType="number-pad"
                editable={!submitting}
                style={[poststyles.input, poststyles.inputText, errors.zipcode ? poststyles.inputError : null]}
                placeholderTextColor="#6B7280"
              />
              <Text style={poststyles.helperText}>
                {errors.zipcode || 'We use this to show your request to nearby providers.'}
              </Text>
            </>
          )}

          {!!message && <Text style={poststyles.errorText}>{message}</Text>}
        </View>

          <View style={poststyles.navRow}>
            <Pressable
              accessibilityRole="button"
              onPress={goBack}
              disabled={submitting || step === STEPS.ITEM}
              style={({ pressed }) => [
                poststyles.navButton, // NEW: makes Back take proportional width of the whole section
                secondaryButtonStyle(submitting || step === STEPS.ITEM),
                pressed && !(submitting || step === STEPS.ITEM) && poststyles.navButtonPressed,
              ]}
            >
              <Text
                style={[
                  poststyles.navIcon,
                  submitting || step === STEPS.ITEM ? poststyles.navIconDisabled : poststyles.navIconSecondary,
                ]}
              >
                ←
              </Text>
              <Text
                style={[
                  poststyles.secondaryButtonText,
                  (submitting || step === STEPS.ITEM) && poststyles.buttonTextDisabled,
                ]}
              >
                Back
              </Text>
            </Pressable>

            {step < STEPS.ZIP ? (
              <Pressable
                accessibilityRole="button"
                onPress={goNext}
                disabled={submitting}
                style={({ pressed }) => [
                  poststyles.navButton, // NEW: makes Next take proportional width of the whole section
                  buttonStyle(tabConfig.btnColor, submitting),
                  pressed && !submitting && poststyles.navPrimaryPressed,
                ]}
              >
                <Text style={[poststyles.primaryButtonText, submitting && poststyles.buttonTextDisabledOnPrimary]}>
                  Next
                </Text>
                <Text
                  style={[
                    poststyles.navIcon,
                    poststyles.navIconPrimary,
                    submitting && poststyles.buttonTextDisabledOnPrimary,
                  ]}
                >
                  →
                </Text>
              </Pressable>
            ) : (
              <Pressable
                accessibilityRole="button"
                onPress={handleSubmit}
                disabled={submitting}
                style={({ pressed }) => [
                  poststyles.navButton, // NEW: makes Submit take the proportional width too
                  buttonStyle(tabConfig.btnColor, submitting),
                  pressed && !submitting && poststyles.navPrimaryPressed,
                ]}
              >
                <Text style={[poststyles.primaryButtonText, submitting && poststyles.buttonTextDisabledOnPrimary]}>
                  {submitting ? 'Submitting…' : tabConfig.btnLabel}
                </Text>
              </Pressable>
            )}
          </View>
      </View>
    </ScrollView>
</KeyboardAvoidingView>
  );
}

// ... existing code ...

